// bp-cal-roster-v1 — deterministic per-person calendar colors.
//
// The calendar view previously colored team members from a hardcoded
// `employeeColors` map (src/data.ts); anyone NOT in that map collapsed to a
// single gray/blue default. Once the roster became dynamic (any @botpros.ai
// login appears), that meant every brand-new teammate rendered the SAME color.
// This helper derives a STABLE, distinct color from a hash of the person's name
// so new teammates get their own hue automatically, while the curated
// `employeeColors` entries still win as overrides. Pure + unit-tested.

// Reuses the CRM's existing 8-hue avatar palette (App.tsx tcwColorFromId) so the
// dots read consistently with the rest of the app.
export const EMPLOYEE_COLOR_PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#ea580c',
  '#9333ea', '#0891b2', '#d97706', '#db2777',
] as const

// Small, stable string hash (djb-ish, 32-bit). Deterministic across isolates and
// browsers so the same name always maps to the same palette slot.
function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

// Deterministic palette color for an arbitrary key (name or email).
export function hashColorFor(key: string | null | undefined): string {
  const k = (key || '').trim().toLowerCase()
  if (!k) return EMPLOYEE_COLOR_PALETTE[0]
  return EMPLOYEE_COLOR_PALETTE[hashString(k) % EMPLOYEE_COLOR_PALETTE.length]
}

// Resolve a person's calendar color: the curated override map wins (matched by
// full name, then first name), otherwise a deterministic hash color so a
// brand-new teammate still renders a stable, distinct hue instead of a shared
// default.
export function resolveEmployeeColor(
  name: string | null | undefined,
  overrides: Record<string, string> = {},
): string {
  const n = (name || '').trim()
  if (n) {
    if (overrides[n]) return overrides[n]
    const first = n.split(/\s+/)[0]
    if (first && overrides[first]) return overrides[first]
  }
  return hashColorFor(n)
}
